import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Pagination from '../components/Pagination';
import GlobalGanttChart from '../components/GlobalGanttChart';
import {
  ResponsiveContainer,
  PieChart, Pie, Cell, Tooltip as ReTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  LineChart, Line, ScatterChart, Scatter, ZAxis,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import type {
  AdminAnalytics, StructureStats, ProcedureRisque,
  TacheEnRetard, UserProductivity, ProcedureProche,
  ProcedureAnnee, ProcedureSansTache, AgentSansTache, TopProcedure,
  Tache, Structure,
} from '../types';
import {
  ClipboardDocumentListIcon, CheckCircleIcon, BuildingOfficeIcon,
  UsersIcon, ExclamationTriangleIcon, FireIcon, ClockIcon,
  ArrowPathIcon, ChartBarSquareIcon, ArrowTrendingUpIcon,
  UserCircleIcon, InformationCircleIcon, ExclamationCircleIcon,
  LightBulbIcon, ShieldExclamationIcon, ChartPieIcon,
  UserGroupIcon, Squares2X2Icon, ViewColumnsIcon, ArrowRightIcon,
  BoltIcon, CheckBadgeIcon, FlagIcon,
} from '@heroicons/react/24/outline';

/* ═══════════════════════════════════════════════════════════
   Types locaux
═══════════════════════════════════════════════════════════ */
type TabId = 'overview' | 'structures' | 'risks' | 'ponderation' | 'agents' | 'gantt';

interface Recommendation {
  level: 'critical' | 'warning' | 'info';
  title: string;
  detail: string;
  count: number;
}

/* ═══════════════════════════════════════════════════════════
   Helpers
═══════════════════════════════════════════════════════════ */
const fmt = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

function computeHealthScore(data: AdminAnalytics): number {
  const { kpis, etatGlobal } = data;
  let score = 100;
  if (kpis.totalProcedures > 0) {
    const retardRatio = kpis.tachesEnRetard / Math.max(kpis.totalTaches, 1);
    score -= Math.min(25, retardRatio * 100);
    score -= Math.min(15, (kpis.proceduresCritiques / kpis.totalProcedures) * 50);
    score -= Math.min(10, (etatGlobal.proceduresSansTaches / kpis.totalProcedures) * 30);
    score += Math.min(10, (kpis.tauxCompletionGlobal / 100) * 10);
  }
  return Math.max(0, Math.min(100, Math.round(score)));
}

function buildRecommendations(data: AdminAnalytics): Recommendation[] {
  const recs: Recommendation[] = [];
  const { kpis, etatGlobal, parStructure } = data;

  if (kpis.proceduresCritiques > 0)
    recs.push({ level: 'critical', count: kpis.proceduresCritiques,
      title: 'Procédures critiques à traiter en urgence',
      detail: `${kpis.proceduresCritiques} procédure${kpis.proceduresCritiques > 1 ? 's' : ''} à moins de 50 % d'avancement avec échéance ≤ 30 jours.` });

  if (kpis.tachesEnRetard > 0)
    recs.push({ level: 'critical', count: kpis.tachesEnRetard,
      title: 'Tâches en retard à débloquer',
      detail: `${kpis.tachesEnRetard} tâche${kpis.tachesEnRetard > 1 ? 's' : ''} dépassent leur date de fin. Identifier les blocages et réaffecter si nécessaire.` });

  if (etatGlobal.proceduresSansTaches > 0)
    recs.push({ level: 'warning', count: etatGlobal.proceduresSansTaches,
      title: 'Procédures sans planification',
      detail: `${etatGlobal.proceduresSansTaches} procédure${etatGlobal.proceduresSansTaches > 1 ? 's' : ''} n'ont aucune tâche définie. La progression ne peut pas être mesurée.` });

  if (etatGlobal.agentsSansTache > 0)
    recs.push({ level: 'warning', count: etatGlobal.agentsSansTache,
      title: 'Agents disponibles à affecter',
      detail: `${etatGlobal.agentsSansTache} agent${etatGlobal.agentsSansTache > 1 ? 's' : ''} n'ont aucune tâche assignée. Optimiser la répartition de la charge de travail.` });

  const lowScore = parStructure.filter(s => s.scorePonderationMoyen < 30 && s.nbProcedures > 0);
  if (lowScore.length > 0)
    recs.push({ level: 'warning', count: lowScore.length,
      title: 'Structures avec faible score de pondération',
      detail: `${lowScore.length} structure${lowScore.length > 1 ? 's' : ''} ont un score pondération moyen < 30/100. Accompagner le remplissage des fiches.` });

  const highRisk = parStructure.filter(s => s.riskScore >= 60);
  if (highRisk.length > 0)
    recs.push({ level: 'warning', count: highRisk.length,
      title: 'Structures à risque élevé',
      detail: `${highRisk.length} structure${highRisk.length > 1 ? 's' : ''} présentent un score de risque ≥ 60. Renforcer le suivi.` });

  if (kpis.tauxCompletionGlobal < 40 && kpis.totalProcedures > 0)
    recs.push({ level: 'info', count: kpis.totalProcedures,
      title: 'Taux de complétion global faible',
      detail: `Le taux de complétion moyen est de ${kpis.tauxCompletionGlobal} %. Organiser des revues de suivi par structure.` });

  if (kpis.scorePonderationMoyen < 50 && kpis.totalProcedures > 0)
    recs.push({ level: 'info', count: 1,
      title: 'Score pondération moyen insuffisant',
      detail: `Le score moyen est de ${kpis.scorePonderationMoyen}/100. Des formations ou un accompagnement peuvent améliorer la qualité des fiches.` });

  return recs;
}

/* ═══════════════════════════════════════════════════════════
   Composants réutilisables
═══════════════════════════════════════════════════════════ */
const RiskBadge: React.FC<{ level: string }> = ({ level }) => {
  const map: Record<string, { cls: string; label: string }> = {
    HIGH:   { cls: 'bg-red-100 text-red-700 border border-red-200',         label: 'Critique' },
    MEDIUM: { cls: 'bg-orange-100 text-orange-700 border border-orange-200', label: 'Modéré' },
    LOW:    { cls: 'bg-green-100 text-green-700 border border-green-200',    label: 'Faible' },
  };
  const { cls, label } = map[level] ?? map.LOW;
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{label}</span>;
};

const ComplexiteBadge: React.FC<{ c: string }> = ({ c }) => {
  const map: Record<string, { cls: string; label: string }> = {
    difficile: { cls: 'bg-red-50 text-red-600',     label: 'Difficile' },
    moyen:     { cls: 'bg-yellow-50 text-yellow-700', label: 'Moyen' },
    facile:    { cls: 'bg-green-50 text-green-700',   label: 'Facile' },
  };
  const { cls, label } = map[c] ?? map.moyen;
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{label}</span>;
};

const MiniProgress: React.FC<{ value: number }> = ({ value }) => {
  const color = value >= 75 ? 'bg-green-500' : value >= 40 ? 'bg-yellow-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-xs text-gray-500 w-7 text-right">{value}%</span>
    </div>
  );
};

const KpiCard: React.FC<{
  title: string; value: string | number; sub?: string;
  icon: React.ElementType; iconBg: string; iconColor: string; alert?: boolean;
  onClick?: () => void;
}> = ({ title, value, sub, icon: Icon, iconBg, iconColor, alert, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-2xl shadow-sm border ${alert ? 'border-red-200' : 'border-gray-100'} p-5 flex items-center gap-4
      ${onClick ? 'cursor-pointer hover:shadow-md hover:border-primary-200 transition-all' : ''}`}
  >
    <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
      <Icon className={`h-6 w-6 ${iconColor}`} />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide truncate">{title}</p>
      <p className={`text-2xl font-bold mt-0.5 ${alert ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      {onClick && <p className="text-[10px] text-primary-500 mt-1 flex items-center gap-0.5">Voir détails <ArrowRightIcon className="h-2.5 w-2.5" /></p>}
    </div>
  </div>
);

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; action?: React.ReactNode }> = ({
  title, subtitle, children, action,
}) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
      <div>
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-10 text-gray-400">
    <ChartBarSquareIcon className="h-10 w-10 mb-2 opacity-30" />
    <p className="text-sm">{message}</p>
  </div>
);

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse">
    <div className="flex gap-4 items-center">
      <div className="w-12 h-12 bg-gray-100 rounded-xl" />
      <div className="flex-1 space-y-2">
        <div className="h-3 bg-gray-100 rounded w-2/3" />
        <div className="h-6 bg-gray-100 rounded w-1/3" />
      </div>
    </div>
  </div>
);

const CustomPieTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg px-3 py-2 text-sm">
      <p className="font-semibold text-gray-900">{payload[0].name}</p>
      <p className="text-gray-600">{payload[0].value}</p>
    </div>
  );
};

const CustomBarTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg px-3 py-2 text-sm">
      <p className="font-semibold text-gray-800 mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color }} className="text-xs">{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

/* ── Jauge santé ── */
const HealthGauge: React.FC<{ score: number }> = ({ score }) => {
  const color = score >= 70 ? '#16a34a' : score >= 45 ? '#d97706' : '#dc2626';
  const label = score >= 70 ? 'Bonne santé' : score >= 45 ? 'Attention requise' : 'Situation critique';
  const bg    = score >= 70 ? 'bg-green-50 border-green-200' : score >= 45 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
  const textCls = score >= 70 ? 'text-green-700' : score >= 45 ? 'text-amber-700' : 'text-red-700';
  const pct = score / 100;
  const r = 52, cx = 64, cy = 64;
  const circumference = Math.PI * r;
  const offset = circumference * (1 - pct);

  return (
    <div className={`flex flex-col items-center justify-center p-6 rounded-2xl border ${bg}`}>
      <svg width="128" height="80" viewBox="0 0 128 80">
        {/* Track */}
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke="#e5e7eb" strokeWidth="12" strokeLinecap="round" />
        {/* Fill */}
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
        <text x={cx} y={cy - 4} textAnchor="middle" fill={color} fontSize="22" fontWeight="800">{score}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill="#9ca3af" fontSize="10">/100</text>
      </svg>
      <p className={`text-sm font-bold mt-1 ${textCls}`}>{label}</p>
      <p className="text-xs text-gray-400 mt-0.5">Indice de santé global</p>
    </div>
  );
};

/* ── Carte recommandation ── */
const RecCard: React.FC<{ rec: Recommendation }> = ({ rec }) => {
  const map = {
    critical: { bg: 'bg-red-50 border-red-200', badge: 'bg-red-600 text-white', icon: '🔴', label: 'Urgent' },
    warning:  { bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-500 text-white', icon: '🟡', label: 'Important' },
    info:     { bg: 'bg-blue-50 border-blue-200', badge: 'bg-blue-500 text-white', icon: '🔵', label: 'À surveiller' },
  };
  const s = map[rec.level];
  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border ${s.bg}`}>
      <span className="text-lg flex-shrink-0 mt-0.5">{s.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-sm font-semibold text-gray-900">{rec.title}</p>
          <span className={`flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${s.badge}`}>{s.label}</span>
        </div>
        <p className="text-xs text-gray-600 leading-relaxed">{rec.detail}</p>
      </div>
      <span className="flex-shrink-0 text-lg font-black text-gray-300">{rec.count}</span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Tabs
═══════════════════════════════════════════════════════════ */
const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview',    label: 'Vue d\'ensemble', icon: Squares2X2Icon },
  { id: 'structures',  label: 'Structures',      icon: BuildingOfficeIcon },
  { id: 'risks',       label: 'Risques & Alertes', icon: ShieldExclamationIcon },
  { id: 'ponderation', label: 'Pondération',     icon: ChartPieIcon },
  { id: 'agents',      label: 'Agents & RH',     icon: UserGroupIcon },
  { id: 'gantt',       label: 'Gantt global',    icon: ViewColumnsIcon },
];

/* ═══════════════════════════════════════════════════════════
   Page principale
═══════════════════════════════════════════════════════════ */
const Analytique: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData]       = useState<AdminAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]     = useState('');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  /* Gantt global */
  const [ganttTaches, setGanttTaches] = useState<Tache[]>([]);
  const [ganttStructures, setGanttStructures] = useState<Structure[]>([]);
  const [ganttLoading, setGanttLoading] = useState(false);
  const [ganttLoaded, setGanttLoaded] = useState(false);

  /* pagination states */
  const [structPage, setStructPage] = useState(0);
  const [pstPage, setPstPage]       = useState(0);
  const [astPage, setAstPage]       = useState(0);
  const [riskPage, setRiskPage]     = useState(0);
  const [retardPage, setRetardPage] = useState(0);
  const STRUCT_SIZE = 8, LIST_SIZE = 10;

  const load = useCallback(async () => {
    setIsLoading(true); setError('');
    try {
      const [result, structuresData] = await Promise.all([
        apiService.getAdminAnalytics(),
        apiService.getStructures(),
      ]);
      setData(result);
      setGanttStructures(structuresData);
      setLastRefresh(new Date());
    } catch {
      setError('Impossible de charger les données analytiques.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadGantt = useCallback(async () => {
    if (ganttLoaded) return;
    setGanttLoading(true);
    try {
      const taches = await apiService.getAllTachesGlobal();
      setGanttTaches(taches);
      setGanttLoaded(true);
    } catch {
      /* silently fail */
    } finally {
      setGanttLoading(false);
    }
  }, [ganttLoaded]);

  useEffect(() => {
    if (!isSuperAdmin()) { navigate('/'); return; }
    load();
  }, []);

  useEffect(() => {
    if (activeTab === 'gantt') loadGantt();
  }, [activeTab, loadGantt]);

  /* ── Loading ── */
  if (isLoading) return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse h-64">
            <div className="h-4 bg-gray-100 rounded w-1/3 mb-4" />
            <div className="h-full bg-gray-50 rounded-xl" />
          </div>
        ))}
      </div>
    </div>
  );

  /* ── Error ── */
  if (error || !data) return (
    <div className="flex flex-col items-center justify-center py-20">
      <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mb-3" />
      <p className="text-gray-600 font-medium mb-4">{error || 'Données non disponibles.'}</p>
      <button onClick={load}
        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-xl hover:bg-primary-700 transition-colors">
        <ArrowPathIcon className="h-4 w-4" /> Réessayer
      </button>
    </div>
  );

  const { kpis, etatGlobal, distributionEtatTaches, distributionComplexite, distributionScores,
    criteresPonderation, parStructure, proceduresRisque, topTachesEnRetard,
    tachesParUtilisateur, proceduresProchesEcheance, proceduresParAnnee,
    proceduresSansTaches, agentsSansTache, topProcedures, bottomProcedures } = data;

  const healthScore   = computeHealthScore(data);
  const recommendations = buildRecommendations(data);

  /* chart data */
  const structChartData = parStructure.slice(0, 10).map(s => ({
    name: s.sigle || s.structureNom.substring(0, 8),
    Progression: s.progressionMoyenne, Score: s.scorePonderationMoyen, Risque: s.riskScore,
  }));
  const userChartData = tachesParUtilisateur.slice(0, 10).map(u => ({
    name: `${u.prenom.charAt(0)}. ${u.nom}`,
    Total: u.nbTachesTotal, Terminées: u.nbTachesTerminees,
    Taux: u.tauxCompletion,
  }));
  const radarData = criteresPonderation.map(c => ({
    critere: c.critere.replace(' & ', '/').replace('exploitation', 'expl.'),
    taux: c.tauxRemplissage, score: c.moyenneScore,
  }));
  /* scatter: progression vs score par structure */
  const scatterData = parStructure.map(s => ({
    x: s.progressionMoyenne, y: s.scorePonderationMoyen,
    z: s.nbProcedures, name: s.sigle || s.structureNom,
  }));

  /* ══════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════ */
  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Tableau Analytique</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Analyse avancée · mis à jour à {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <button onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors self-start">
          <ArrowPathIcon className="h-4 w-4" /> Actualiser
        </button>
      </div>

      {/* ── KPIs (toujours visibles) ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Procédures"        value={kpis.totalProcedures}
          icon={ClipboardDocumentListIcon} iconBg="bg-blue-50"    iconColor="text-blue-600"
          onClick={() => navigate('/procedures')} />
        <KpiCard title="Tâches totales"    value={kpis.totalTaches}
          icon={CheckCircleIcon}           iconBg="bg-indigo-50"  iconColor="text-indigo-600"
          onClick={() => setActiveTab('risks')} />
        <KpiCard title="Structures"        value={kpis.totalStructures}
          icon={BuildingOfficeIcon}        iconBg="bg-purple-50"  iconColor="text-purple-600"
          onClick={() => setActiveTab('structures')} />
        <KpiCard title="Agents"            value={kpis.totalUsers}
          icon={UsersIcon}                 iconBg="bg-cyan-50"    iconColor="text-cyan-600"
          onClick={() => setActiveTab('agents')} />
        <KpiCard title="Taux de complétion" value={`${kpis.tauxCompletionGlobal}%`}
          sub="moyenne toutes procédures"
          icon={ArrowTrendingUpIcon}       iconBg="bg-green-50"   iconColor="text-green-600"
          onClick={() => setActiveTab('structures')} />
        <KpiCard title="Score pondération"  value={`${kpis.scorePonderationMoyen}/100`}
          sub="moyenne toutes fiches"
          icon={ChartBarSquareIcon}        iconBg="bg-teal-50"    iconColor="text-teal-600"
          onClick={() => setActiveTab('ponderation')} />
        <KpiCard title="Tâches en retard"   value={kpis.tachesEnRetard}
          icon={ExclamationTriangleIcon}   iconBg="bg-orange-50"  iconColor="text-orange-600"
          alert={kpis.tachesEnRetard > 0}
          onClick={() => setActiveTab('risks')} />
        <KpiCard title="Procédures critiques" value={kpis.proceduresCritiques}
          sub="< 50 % · échéance ≤ 30 j"
          icon={FireIcon}                  iconBg="bg-red-50"     iconColor="text-red-600"
          alert={kpis.proceduresCritiques > 0}
          onClick={() => setActiveTab('risks')} />
      </div>

      {/* ── Tabs ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Tab bar */}
        <div className="flex overflow-x-auto border-b border-gray-100 scrollbar-none">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0
                  ${active
                    ? 'border-primary-600 text-primary-700 bg-primary-50/40'
                    : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {/* badges d'alerte */}
                {tab.id === 'risks' && (kpis.proceduresCritiques + kpis.tachesEnRetard) > 0 && (
                  <span className="ml-1 min-w-[18px] h-4.5 bg-red-500 text-white text-[10px] font-bold px-1 rounded-full flex items-center justify-center">
                    {kpis.proceduresCritiques + kpis.tachesEnRetard}
                  </span>
                )}
                {tab.id === 'overview' && recommendations.filter(r => r.level === 'critical').length > 0 && (
                  <span className="ml-1 w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="p-6 space-y-6">

          {/* ════════════════════════════════════════
              ONG 1 — Vue d'ensemble
          ════════════════════════════════════════ */}
          {activeTab === 'overview' && (
            <div className="space-y-6">

              {/* Santé + État global */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <HealthGauge score={healthScore} />

                <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Terminées',      value: etatGlobal.proceduresTerminees, cls: 'bg-green-50 text-green-700 border-green-200',   dot: 'bg-green-500' },
                    { label: 'En cours',       value: etatGlobal.proceduresEnCours,   cls: 'bg-blue-50 text-blue-700 border-blue-200',      dot: 'bg-blue-500' },
                    { label: 'En attente',     value: etatGlobal.proceduresEnAttente, cls: 'bg-gray-50 text-gray-700 border-gray-200',       dot: 'bg-gray-400' },
                    { label: 'Sans tâches',    value: etatGlobal.proceduresSansTaches,
                      cls: etatGlobal.proceduresSansTaches > 0 ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-gray-50 text-gray-500 border-gray-200',
                      dot: 'bg-orange-400' },
                    { label: 'Agents libres',  value: etatGlobal.agentsSansTache,
                      cls: etatGlobal.agentsSansTache > 0 ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-gray-50 text-gray-500 border-gray-200',
                      dot: 'bg-yellow-400' },
                    { label: 'Tâches en retard', value: kpis.tachesEnRetard,
                      cls: kpis.tachesEnRetard > 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-gray-50 text-gray-500 border-gray-200',
                      dot: 'bg-red-500' },
                  ].map(item => (
                    <div key={item.label} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border ${item.cls}`}>
                      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${item.dot}`} />
                      <div>
                        <p className="text-xl font-bold leading-none">{item.value}</p>
                        <p className="text-[11px] font-medium mt-0.5 opacity-80">{item.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommandations */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <LightBulbIcon className="h-5 w-5 text-amber-500" />
                  <h3 className="text-sm font-bold text-gray-900">
                    Recommandations ({recommendations.length})
                  </h3>
                  {recommendations.filter(r => r.level === 'critical').length > 0 && (
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full">
                      {recommendations.filter(r => r.level === 'critical').length} urgent{recommendations.filter(r => r.level === 'critical').length > 1 ? 'es' : 'e'}
                    </span>
                  )}
                </div>
                {recommendations.length === 0 ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-700 font-medium">Aucune action urgente identifiée. La situation est sous contrôle.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {recommendations.map((rec, i) => <RecCard key={i} rec={rec} />)}
                  </div>
                )}
              </div>

              {/* ── Plan d'action immédiat ── */}
              {recommendations.filter(r => r.level === 'critical').length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <BoltIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <h3 className="text-sm font-bold text-red-800">Plan d'action immédiat</h3>
                    <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full font-bold ml-auto">
                      {recommendations.filter(r => r.level === 'critical').length} action{recommendations.filter(r => r.level === 'critical').length > 1 ? 's' : ''} urgente{recommendations.filter(r => r.level === 'critical').length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {recommendations.filter(r => r.level === 'critical').map((rec, i) => (
                      <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-3 border border-red-100">
                        <span className="w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{rec.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{rec.detail}</p>
                        </div>
                        <button onClick={() => setActiveTab('risks')}
                          className="flex-shrink-0 flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700">
                          Voir <ArrowRightIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Matrice décisionnelle ── */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
                  <FlagIcon className="h-4 w-4 text-gray-500" />
                  <h3 className="text-sm font-bold text-gray-900">Tableau de bord décisionnel</h3>
                  <span className="text-xs text-gray-400 ml-1">— indicateurs clés pour la prise de décision</span>
                </div>
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Taux global */}
                  {[
                    {
                      label: 'Avancement global',
                      value: `${kpis.tauxCompletionGlobal}%`,
                      status: kpis.tauxCompletionGlobal >= 70 ? 'good' : kpis.tauxCompletionGlobal >= 40 ? 'warn' : 'crit',
                      action: kpis.tauxCompletionGlobal < 40 ? 'Organiser des revues de suivi urgentes par structure' : kpis.tauxCompletionGlobal < 70 ? 'Renforcer le suivi des procédures en cours' : 'Maintenir la dynamique actuelle',
                      icon: ArrowTrendingUpIcon,
                      tab: 'structures' as TabId,
                    },
                    {
                      label: 'Score pondération moyen',
                      value: `${kpis.scorePonderationMoyen}/100`,
                      status: kpis.scorePonderationMoyen >= 70 ? 'good' : kpis.scorePonderationMoyen >= 40 ? 'warn' : 'crit',
                      action: kpis.scorePonderationMoyen < 40 ? 'Formation urgente au remplissage des fiches de pondération' : kpis.scorePonderationMoyen < 70 ? 'Accompagnement ciblé des structures à faible score' : 'Partager les bonnes pratiques',
                      icon: ChartBarSquareIcon,
                      tab: 'ponderation' as TabId,
                    },
                    {
                      label: 'Taux d\'affectation agents',
                      value: kpis.totalUsers > 0 ? `${Math.round(((kpis.totalUsers - etatGlobal.agentsSansTache) / kpis.totalUsers) * 100)}%` : '—',
                      status: kpis.totalUsers > 0 && etatGlobal.agentsSansTache === 0 ? 'good' : etatGlobal.agentsSansTache < kpis.totalUsers * 0.3 ? 'warn' : 'crit',
                      action: etatGlobal.agentsSansTache > 0 ? `${etatGlobal.agentsSansTache} agent${etatGlobal.agentsSansTache > 1 ? 's' : ''} disponible${etatGlobal.agentsSansTache > 1 ? 's' : ''} à mobiliser sur les tâches en retard` : 'Tous les agents sont affectés',
                      icon: UsersIcon,
                      tab: 'agents' as TabId,
                    },
                    {
                      label: 'Procédures sans tâches',
                      value: etatGlobal.proceduresSansTaches,
                      status: etatGlobal.proceduresSansTaches === 0 ? 'good' : etatGlobal.proceduresSansTaches <= 3 ? 'warn' : 'crit',
                      action: etatGlobal.proceduresSansTaches > 0 ? 'Planifier immédiatement les tâches pour ces procédures non suivies' : 'Toutes les procédures ont des tâches planifiées',
                      icon: ExclamationCircleIcon,
                      tab: 'risks' as TabId,
                    },
                    {
                      label: 'Procédures critiques',
                      value: kpis.proceduresCritiques,
                      status: kpis.proceduresCritiques === 0 ? 'good' : kpis.proceduresCritiques <= 2 ? 'warn' : 'crit',
                      action: kpis.proceduresCritiques > 0 ? 'Réunion de crise recommandée avec les responsables des structures concernées' : 'Aucune procédure en situation critique',
                      icon: FireIcon,
                      tab: 'risks' as TabId,
                    },
                    {
                      label: 'Tâches en retard',
                      value: kpis.tachesEnRetard,
                      status: kpis.tachesEnRetard === 0 ? 'good' : kpis.tachesEnRetard <= 5 ? 'warn' : 'crit',
                      action: kpis.tachesEnRetard > 0 ? 'Identifier les blocages, réaffecter ou réviser les délais selon la criticité' : 'Aucune tâche en retard',
                      icon: ClockIcon,
                      tab: 'risks' as TabId,
                    },
                  ].map(item => {
                    const colorMap = {
                      good: { bg: 'bg-green-50 border-green-200', value: 'text-green-700', dot: 'bg-green-500', icon: 'bg-green-100 text-green-600', label: 'OK' },
                      warn: { bg: 'bg-amber-50 border-amber-200', value: 'text-amber-700', dot: 'bg-amber-400', icon: 'bg-amber-100 text-amber-600', label: 'Attention' },
                      crit: { bg: 'bg-red-50 border-red-200',    value: 'text-red-700',   dot: 'bg-red-500',   icon: 'bg-red-100 text-red-600',   label: 'Urgent' },
                    };
                    const colors = colorMap[item.status as keyof typeof colorMap];
                    return (
                      <button key={item.label} onClick={() => setActiveTab(item.tab)}
                        className={`rounded-xl border p-4 ${colors.bg} text-left w-full hover:shadow-sm transition-shadow`}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-8 h-8 rounded-lg ${colors.icon} flex items-center justify-center flex-shrink-0`}>
                            <item.icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{item.label}</p>
                            <p className={`text-xl font-black leading-tight ${colors.value}`}>{item.value}</p>
                          </div>
                          <span className={`flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white ${colors.dot}`}>
                            {colors.label}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-600 leading-relaxed border-t border-current/10 pt-2">{item.action}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Points de vigilance pour le prochain comité ── */}
              {(kpis.proceduresCritiques > 0 || kpis.tachesEnRetard > 0 || etatGlobal.proceduresSansTaches > 0) && (
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
                    <CheckBadgeIcon className="h-4 w-4 text-primary-600" />
                    <h3 className="text-sm font-bold text-gray-900">Points à inscrire à l'ordre du jour</h3>
                  </div>
                  <div className="p-5">
                    <ol className="space-y-3 text-sm text-gray-700">
                      {kpis.proceduresCritiques > 0 && (
                        <li className="flex items-start gap-3">
                          <span className="w-5 h-5 rounded-full bg-red-100 text-red-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                          <span>Revue des <strong>{kpis.proceduresCritiques}</strong> procédure{kpis.proceduresCritiques > 1 ? 's' : ''} critiques : présenter l'état d'avancement et décider d'une prolongation ou d'une réallocation de ressources.</span>
                        </li>
                      )}
                      {kpis.tachesEnRetard > 0 && (
                        <li className="flex items-start gap-3">
                          <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{kpis.proceduresCritiques > 0 ? 2 : 1}</span>
                          <span>Déblocage des <strong>{kpis.tachesEnRetard}</strong> tâche{kpis.tachesEnRetard > 1 ? 's' : ''} en retard : identifier les causes (ressources, technique, organisationnel) et valider un plan de rattrapage.</span>
                        </li>
                      )}
                      {etatGlobal.proceduresSansTaches > 0 && (
                        <li className="flex items-start gap-3">
                          <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{(kpis.proceduresCritiques > 0 ? 1 : 0) + (kpis.tachesEnRetard > 0 ? 1 : 0) + 1}</span>
                          <span>Planification des <strong>{etatGlobal.proceduresSansTaches}</strong> procédure{etatGlobal.proceduresSansTaches > 1 ? 's' : ''} sans tâches : chaque structure responsable doit présenter un plan de travail détaillé.</span>
                        </li>
                      )}
                      {kpis.scorePonderationMoyen < 50 && (
                        <li className="flex items-start gap-3">
                          <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{(kpis.proceduresCritiques > 0 ? 1 : 0) + (kpis.tachesEnRetard > 0 ? 1 : 0) + (etatGlobal.proceduresSansTaches > 0 ? 1 : 0) + 1}</span>
                          <span>Amélioration des fiches de pondération : le score moyen de <strong>{kpis.scorePonderationMoyen}/100</strong> est insuffisant. Planifier une session de formation ou d'accompagnement méthodologique.</span>
                        </li>
                      )}
                    </ol>
                  </div>
                </div>
              )}

              {/* Distribution tâches */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Section title="État des tâches" subtitle="Répartition par statut">
                  {distributionEtatTaches.every(d => d.value === 0) ? <EmptyState message="Aucune tâche" /> : (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={distributionEtatTaches} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                          {distributionEtatTaches.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <ReTooltip content={<CustomPieTooltip />} />
                        <Legend formatter={v => <span className="text-xs text-gray-600">{v}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Section>
                <Section title="Complexité des tâches" subtitle="Répartition par difficulté">
                  {distributionComplexite.every(d => d.value === 0) ? <EmptyState message="Aucune tâche" /> : (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={distributionComplexite} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                          {distributionComplexite.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <ReTooltip content={<CustomPieTooltip />} />
                        <Legend formatter={v => <span className="text-xs text-gray-600">{v}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Section>
              </div>

              {/* Évolution par année */}
              {proceduresParAnnee.length > 0 && (
                <Section title="Évolution par année" subtitle="Progression et score moyen par année">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={proceduresParAnnee} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="annee" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                      <YAxis yAxisId="l" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                      <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tickFormatter={v => `${v}%`}
                        tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                      <ReTooltip content={<CustomBarTooltip />} />
                      <Legend formatter={v => <span className="text-xs text-gray-600">{v}</span>} />
                      <Bar yAxisId="l" dataKey="nbProcedures" name="Total" fill="#E0E7FF" radius={[4,4,0,0]} maxBarSize={32} />
                      <Bar yAxisId="l" dataKey="nbTerminees"  name="Terminées" fill="#6366F1" radius={[4,4,0,0]} maxBarSize={32} />
                      <Bar yAxisId="r" dataKey="progressionMoyenne" name="Progression moy." fill="#10B981" radius={[4,4,0,0]} maxBarSize={32} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          {['Année','Procédures','Terminées','En cours','Retards','Progression','Score'].map(h => (
                            <th key={h} className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-4 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {proceduresParAnnee.map((r: ProcedureAnnee) => (
                          <tr key={r.annee} className="hover:bg-gray-50">
                            <td className="py-2 pr-4 font-bold text-gray-800">{r.annee}</td>
                            <td className="py-2 pr-4 text-gray-700">{r.nbProcedures}</td>
                            <td className="py-2 pr-4 text-green-600 font-semibold">{r.nbTerminees}</td>
                            <td className="py-2 pr-4 text-blue-600">{r.nbEnCours}</td>
                            <td className="py-2 pr-4"><span className={r.nbTachesEnRetard > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}>{r.nbTachesEnRetard}</span></td>
                            <td className="py-2 pr-4"><MiniProgress value={r.progressionMoyenne} /></td>
                            <td className="py-2 text-gray-600">{r.scoreMoyen}<span className="text-gray-400 text-xs">/100</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════
              ONG 2 — Structures
          ════════════════════════════════════════ */}
          {activeTab === 'structures' && (
            <div className="space-y-6">

              {/* Scatter : Progression vs Score */}
              <Section title="Carte de positionnement des structures"
                subtitle="Progression (axe X) vs Score pondération (axe Y) — taille = nb procédures">
                {scatterData.length === 0 ? <EmptyState message="Aucune structure" /> : (
                  <ResponsiveContainer width="100%" height={280}>
                    <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" dataKey="x" name="Progression" domain={[0,100]}
                        tickFormatter={v => `${v}%`} tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false}
                        label={{ value: 'Progression (%)', position: 'insideBottom', offset: -4, fill: '#9CA3AF', fontSize: 11 }} />
                      <YAxis type="number" dataKey="y" name="Score pond." domain={[0,100]}
                        tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false}
                        label={{ value: 'Score pond.', angle: -90, position: 'insideLeft', fill: '#9CA3AF', fontSize: 11 }} />
                      <ZAxis type="number" dataKey="z" range={[40, 400]} />
                      <ReTooltip cursor={{ strokeDasharray: '3 3' }}
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0].payload;
                          return (
                            <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs">
                              <p className="font-semibold text-gray-900 mb-1">{d.name}</p>
                              <p className="text-gray-600">Progression : {d.x}%</p>
                              <p className="text-gray-600">Score pond. : {d.y}/100</p>
                              <p className="text-gray-600">Procédures : {d.z}</p>
                            </div>
                          );
                        }}
                      />
                      <Scatter data={scatterData} fill="#6366F1" fillOpacity={0.75} />
                      {/* Quadrant lines */}
                      <ReTooltip />
                    </ScatterChart>
                  </ResponsiveContainer>
                )}
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  {[
                    { q: 'Q1 — Stars', desc: 'Progression ET score élevés → maintenir', cls: 'bg-green-50 border-green-200 text-green-700' },
                    { q: 'Q2 — Effort qualité', desc: 'Progression élevée, score faible → améliorer pondération', cls: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
                    { q: 'Q3 — Priorité absolue', desc: 'Faible progression ET score → intervention urgente', cls: 'bg-red-50 border-red-200 text-red-700' },
                    { q: 'Q4 — Qualité sans vitesse', desc: 'Score élevé, progression faible → accélérer les tâches', cls: 'bg-blue-50 border-blue-200 text-blue-700' },
                  ].map(item => (
                    <div key={item.q} className={`p-2.5 rounded-lg border ${item.cls}`}>
                      <p className="font-semibold">{item.q}</p>
                      <p className="opacity-80">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Bar horizontal */}
              <Section title="Progression vs Score par structure" subtitle="Top 10 structures">
                {parStructure.length === 0 ? <EmptyState message="Aucune structure" /> : (
                  <ResponsiveContainer width="100%" height={Math.max(200, structChartData.length * 40)}>
                    <BarChart data={structChartData} layout="vertical" barCategoryGap="25%">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                      <XAxis type="number" domain={[0,100]} tickFormatter={v => `${v}%`}
                        tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" width={72}
                        tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} />
                      <ReTooltip content={<CustomBarTooltip />} />
                      <Legend formatter={v => <span className="text-xs text-gray-600">{v}</span>} />
                      <Bar dataKey="Progression" fill="#3B82F6" radius={[0,4,4,0]} maxBarSize={14} />
                      <Bar dataKey="Score"       fill="#8B5CF6" radius={[0,4,4,0]} maxBarSize={14} />
                      <Bar dataKey="Risque"      fill="#EF4444" radius={[0,4,4,0]} maxBarSize={14} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Section>

              {/* Table complète structures */}
              <Section title="Détail performance par structure" subtitle="Classées par score de risque décroissant">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        {['Structure','Procédures','Progression','Tâches','En retard','Score pond.','Agents','Risque'].map(h => (
                          <th key={h} className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide pb-2 pr-4 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {parStructure.slice(structPage * STRUCT_SIZE, (structPage + 1) * STRUCT_SIZE).map((s: StructureStats) => (
                        <tr key={s.structureId} className="hover:bg-gray-50 transition-colors">
                          <td className="py-2.5 pr-4">
                            <Link to={`/procedures?structureId=${s.structureId}`} className="font-medium text-gray-900 hover:text-primary-600">{s.sigle || s.structureNom}</Link>
                            {s.sigle && <div className="text-[11px] text-gray-400 truncate max-w-[140px]">{s.structureNom}</div>}
                          </td>
                          <td className="py-2.5 pr-4 text-gray-700">{s.nbProcedures}</td>
                          <td className="py-2.5 pr-4"><MiniProgress value={s.progressionMoyenne} /></td>
                          <td className="py-2.5 pr-4 text-gray-700">{s.nbTachesTotal}</td>
                          <td className="py-2.5 pr-4">
                            <span className={s.nbTachesEnRetard > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}>{s.nbTachesEnRetard}</span>
                          </td>
                          <td className="py-2.5 pr-4">
                            <span className={`font-semibold ${s.scorePonderationMoyen >= 75 ? 'text-green-600' : s.scorePonderationMoyen >= 50 ? 'text-yellow-600' : 'text-red-500'}`}>
                              {s.scorePonderationMoyen}
                            </span>
                            <span className="text-gray-400 text-xs">/100</span>
                          </td>
                          <td className="py-2.5 pr-4 text-gray-700">{s.nbUtilisateurs}</td>
                          <td className="py-2.5">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.riskScore >= 60 ? 'bg-red-100 text-red-700' : s.riskScore >= 30 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                              {s.riskScore}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {parStructure.length > STRUCT_SIZE && (
                  <div className="mt-4">
                    <Pagination page={structPage} totalPages={Math.ceil(parStructure.length / STRUCT_SIZE)}
                      totalElements={parStructure.length} size={STRUCT_SIZE} onPageChange={setStructPage} />
                  </div>
                )}
              </Section>
            </div>
          )}

          {/* ════════════════════════════════════════
              ONG 3 — Risques & Alertes
          ════════════════════════════════════════ */}
          {activeTab === 'risks' && (
            <div className="space-y-6">

              {/* Compteurs risque */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Proc. critiques',    value: kpis.proceduresCritiques, cls: 'bg-red-50 border-red-200 text-red-700' },
                  { label: 'Tâches en retard',   value: kpis.tachesEnRetard,      cls: 'bg-orange-50 border-orange-200 text-orange-700' },
                  { label: 'Proc. sans tâches',  value: etatGlobal.proceduresSansTaches, cls: 'bg-amber-50 border-amber-200 text-amber-700' },
                  { label: 'Proc. proches échéance', value: proceduresProchesEcheance.length, cls: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
                ].map(item => (
                  <div key={item.label} className={`flex flex-col items-center justify-center p-4 rounded-xl border ${item.cls} text-center`}>
                    <p className="text-3xl font-black">{item.value}</p>
                    <p className="text-xs font-medium mt-1 opacity-80">{item.label}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Procédures à risque */}
                <Section title={`Procédures à risque (${proceduresRisque.length})`} subtitle="Classées par score de risque">
                  {proceduresRisque.length === 0 ? <EmptyState message="Aucune procédure à risque" /> : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-100">
                              <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">Procédure</th>
                              <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">Avancement</th>
                              <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3 whitespace-nowrap">J. rest.</th>
                              <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">Retards</th>
                              <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2">Risque</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {proceduresRisque.slice(riskPage * LIST_SIZE, (riskPage + 1) * LIST_SIZE).map((p: ProcedureRisque) => (
                              <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                                <td className="py-2.5 pr-3">
                                  <Link to={`/procedures/${p.id}`} className="font-medium text-gray-900 hover:text-primary-600 line-clamp-1">{p.nom}</Link>
                                  <p className="text-[11px] text-gray-400">{p.sigle || p.structureNom}</p>
                                </td>
                                <td className="py-2.5 pr-3"><MiniProgress value={p.progress} /></td>
                                <td className="py-2.5 pr-3">
                                  <span className={`text-xs font-semibold ${p.joursRestants <= 7 ? 'text-red-600' : p.joursRestants <= 15 ? 'text-orange-500' : 'text-gray-600'}`}>
                                    {p.joursRestants}j
                                  </span>
                                </td>
                                <td className="py-2.5 pr-3">
                                  <span className={p.nbTachesEnRetard > 0 ? 'text-red-600 font-semibold text-xs' : 'text-gray-400 text-xs'}>{p.nbTachesEnRetard}</span>
                                </td>
                                <td className="py-2.5"><RiskBadge level={p.riskLevel} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {proceduresRisque.length > LIST_SIZE && (
                        <div className="mt-3">
                          <Pagination page={riskPage} totalPages={Math.ceil(proceduresRisque.length / LIST_SIZE)}
                            totalElements={proceduresRisque.length} size={LIST_SIZE} onPageChange={setRiskPage} />
                        </div>
                      )}
                    </>
                  )}
                </Section>

                {/* Tâches en retard */}
                <Section title={`Tâches en retard (${topTachesEnRetard.length})`} subtitle="Par nombre de jours de retard">
                  {topTachesEnRetard.length === 0 ? <EmptyState message="Aucune tâche en retard" /> : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-100">
                              <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">Tâche</th>
                              <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">Retard</th>
                              <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">Complexité</th>
                              <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2">Assigné</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {topTachesEnRetard.slice(retardPage * LIST_SIZE, (retardPage + 1) * LIST_SIZE).map((t: TacheEnRetard) => (
                              <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                                <td className="py-2.5 pr-3">
                                  <Link to={`/procedures/${t.procedureId}`} className="font-medium text-gray-900 hover:text-primary-600 line-clamp-1 block">{t.nom}</Link>
                                  <p className="text-[11px] text-gray-400 line-clamp-1">{t.procedureNom}</p>
                                </td>
                                <td className="py-2.5 pr-3"><span className="text-xs font-bold text-red-600">{t.joursRetard}j</span></td>
                                <td className="py-2.5 pr-3"><ComplexiteBadge c={t.complexite} /></td>
                                <td className="py-2.5">
                                  {t.assignedUser ? (
                                    <div className="flex items-center gap-1.5">
                                      <UserCircleIcon className="h-4 w-4 text-gray-400" />
                                      <span className="text-xs text-gray-600">{t.assignedUser}</span>
                                    </div>
                                  ) : <span className="text-xs text-red-400 font-medium">Non assigné</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {topTachesEnRetard.length > LIST_SIZE && (
                        <div className="mt-3">
                          <Pagination page={retardPage} totalPages={Math.ceil(topTachesEnRetard.length / LIST_SIZE)}
                            totalElements={topTachesEnRetard.length} size={LIST_SIZE} onPageChange={setRetardPage} />
                        </div>
                      )}
                    </>
                  )}
                </Section>
              </div>

              {/* Proches échéance */}
              <Section title="Procédures proches de l'échéance" subtitle="Délai ≤ 30 jours, non terminées">
                {proceduresProchesEcheance.length === 0 ? <EmptyState message="Aucune procédure n'arrive à échéance dans les 30 jours" /> : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {proceduresProchesEcheance.map((p: ProcedureProche) => (
                      <div key={p.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors
                        ${p.joursRestants <= 7 ? 'border-red-200 bg-red-50/30' : p.joursRestants <= 15 ? 'border-orange-200 bg-orange-50/20' : 'border-yellow-200 bg-yellow-50/20'}`}>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold
                          ${p.joursRestants <= 7 ? 'bg-red-100 text-red-700' : p.joursRestants <= 15 ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {p.joursRestants}j
                        </div>
                        <div className="flex-1 min-w-0">
                          <Link to={`/procedures/${p.id}`} className="text-sm font-medium text-gray-900 hover:text-primary-600 line-clamp-1">{p.nom}</Link>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[11px] text-gray-400">{p.structureNom}</span>
                            <span className="text-gray-200">·</span>
                            <span className="text-[11px] text-gray-400 flex items-center gap-1">
                              <ClockIcon className="h-3 w-3" />{fmt(p.dateFin)}
                            </span>
                          </div>
                          <MiniProgress value={p.progress} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* Top / Bottom */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Section title="Top 5 — les plus avancées" subtitle="Progression décroissante">
                  {topProcedures.length === 0 ? <EmptyState message="Aucune procédure avec tâches" /> : (
                    <div className="space-y-2.5">
                      {topProcedures.map((p: TopProcedure, i) => (
                        <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-green-50/30 transition-colors">
                          <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{i+1}</span>
                          <div className="flex-1 min-w-0">
                            <Link to={`/procedures/${p.id}`} className="text-sm font-medium text-gray-900 hover:text-primary-600 line-clamp-1">{p.nom}</Link>
                            <p className="text-[11px] text-gray-400">{p.structureNom} · {p.annee}</p>
                            <MiniProgress value={p.progress} />
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className={`text-xs font-semibold ${p.ponderationScore >= 75 ? 'text-green-600' : p.ponderationScore >= 50 ? 'text-yellow-600' : 'text-red-500'}`}>{p.ponderationScore}/100</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
                <Section title="Bottom 5 — les moins avancées" subtitle="Progression croissante — intervention recommandée">
                  {bottomProcedures.length === 0 ? <EmptyState message="Aucune procédure avec tâches" /> : (
                    <div className="space-y-2.5">
                      {bottomProcedures.map((p: TopProcedure, i) => (
                        <div key={p.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${p.nbEnRetard > 0 ? 'border-red-100 bg-red-50/20' : 'border-orange-100 bg-orange-50/10'}`}>
                          <span className="w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{i+1}</span>
                          <div className="flex-1 min-w-0">
                            <Link to={`/procedures/${p.id}`} className="text-sm font-medium text-gray-900 hover:text-primary-600 line-clamp-1">{p.nom}</Link>
                            <p className="text-[11px] text-gray-400">{p.structureNom} · {p.annee}</p>
                            <MiniProgress value={p.progress} />
                          </div>
                          <div className="text-right flex-shrink-0">
                            {p.nbEnRetard > 0 && <p className="text-xs font-semibold text-red-600">{p.nbEnRetard} retard{p.nbEnRetard > 1 ? 's' : ''}</p>}
                            <p className="text-[10px] text-gray-400">{p.joursRestants >= 0 ? `${p.joursRestants}j` : 'Dépassée'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              </div>

              {/* Procédures sans tâches */}
              {proceduresSansTaches.length > 0 && (
                <Section title={`Procédures sans tâches (${proceduresSansTaches.length})`} subtitle="Action requise — aucune tâche planifiée">
                  <div className="flex items-start gap-2 mb-4 bg-orange-50 border border-orange-100 rounded-xl px-4 py-3">
                    <ExclamationCircleIcon className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-orange-700">Ces procédures n'ont aucune tâche. La progression ne peut pas être mesurée.</p>
                  </div>
                  <div className="space-y-2">
                    {proceduresSansTaches.slice(pstPage * LIST_SIZE, (pstPage + 1) * LIST_SIZE).map((p: ProcedureSansTache) => (
                      <div key={p.id} className="flex items-center justify-between p-3 rounded-xl border border-orange-100 hover:bg-orange-50/30 transition-colors">
                        <div className="min-w-0">
                          <Link to={`/procedures/${p.id}`} className="text-sm font-medium text-gray-900 hover:text-primary-600 line-clamp-1">{p.nom}</Link>
                          <p className="text-[11px] text-gray-400">{p.structureNom} · {p.annee}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <span className={`text-xs font-semibold ${p.joursRestants <= 7 ? 'text-red-600' : p.joursRestants <= 30 ? 'text-orange-600' : 'text-gray-500'}`}>
                            {p.joursRestants >= 0 ? `${p.joursRestants}j` : 'Dépassée'}
                          </span>
                          <p className="text-[10px] text-gray-400">score: {p.scorePonderation}/100</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {proceduresSansTaches.length > LIST_SIZE && (
                    <div className="mt-3">
                      <Pagination page={pstPage} totalPages={Math.ceil(proceduresSansTaches.length / LIST_SIZE)}
                        totalElements={proceduresSansTaches.length} size={LIST_SIZE} onPageChange={setPstPage} />
                    </div>
                  )}
                </Section>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════
              ONG 4 — Pondération
          ════════════════════════════════════════ */}
          {activeTab === 'ponderation' && (
            <div className="space-y-6">

              {/* Stat résumé pondération */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Score moyen',    value: `${kpis.scorePonderationMoyen}/100`,
                    cls: kpis.scorePonderationMoyen >= 75 ? 'text-green-600' : kpis.scorePonderationMoyen >= 50 ? 'text-amber-600' : 'text-red-600' },
                  { label: 'Fiches ≥ 75/100', value: parStructure.filter(s => s.scorePonderationMoyen >= 75).length, cls: 'text-green-600' },
                  { label: 'Fiches < 30/100', value: parStructure.filter(s => s.scorePonderationMoyen < 30 && s.nbProcedures > 0).length, cls: 'text-red-600' },
                ].map(item => (
                  <div key={item.label} className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
                    <p className={`text-2xl font-black ${item.cls}`}>{item.value}</p>
                    <p className="text-xs text-gray-500 mt-1">{item.label}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Section title="Distribution des scores" subtitle="Répartition par tranche /100">
                  {distributionScores.every(d => d.count === 0) ? <EmptyState message="Aucune fiche renseignée" /> : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={distributionScores} barCategoryGap="30%">
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                        <ReTooltip formatter={(v: number) => [v, 'Procédures']} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                        <Bar dataKey="count" name="Procédures" radius={[6,6,0,0]} maxBarSize={48}>
                          {distributionScores.map((_, i) => (
                            <Cell key={i} fill={i === 0 ? '#EF4444' : i === 1 ? '#F59E0B' : i === 2 ? '#3B82F6' : '#10B981'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Section>

                <Section title="Radar des critères" subtitle="Taux de remplissage moyen par critère (%)">
                  {criteresPonderation.length === 0 ? <EmptyState message="Aucune fiche renseignée" /> : (
                    <ResponsiveContainer width="100%" height={260}>
                      <RadarChart data={radarData} outerRadius={80}>
                        <PolarGrid stroke="#E5E7EB" />
                        <PolarAngleAxis dataKey="critere" tick={{ fontSize: 10, fill: '#6B7280' }} />
                        <PolarRadiusAxis angle={30} domain={[0,100]} tick={{ fontSize: 9, fill: '#9CA3AF' }} />
                        <Radar name="Taux %" dataKey="taux" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} strokeWidth={2} />
                        <Radar name="Score moy." dataKey="score" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.15} strokeWidth={2} />
                        <Legend formatter={v => <span className="text-xs text-gray-600">{v}</span>} />
                        <ReTooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                      </RadarChart>
                    </ResponsiveContainer>
                  )}
                </Section>
              </div>

              {/* Barres critères */}
              {criteresPonderation.length > 0 && (
                <Section title="Détail par critère de pondération" subtitle="Score moyen et taux de remplissage">
                  <div className="space-y-3">
                    {criteresPonderation.map(c => {
                      const pct = c.tauxRemplissage;
                      const color = pct >= 75 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : pct >= 25 ? 'bg-amber-400' : 'bg-red-400';
                      return (
                        <div key={c.critere}>
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-medium text-gray-700">{c.critere}</p>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-gray-400">Score moy. : <span className="font-semibold text-gray-700">{c.moyenneScore}/{c.max}</span></span>
                              <span className="text-xs font-bold text-gray-700 w-10 text-right">{pct}%</span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Section>
              )}

              {/* Structures avec faibles scores */}
              {parStructure.filter(s => s.scorePonderationMoyen < 50 && s.nbProcedures > 0).length > 0 && (
                <Section title="Structures nécessitant un accompagnement" subtitle="Score pondération moyen < 50/100">
                  <div className="flex items-start gap-2 mb-4 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                    <LightBulbIcon className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">Ces structures ont des scores de pondération insuffisants. Un accompagnement ou une formation sur le remplissage des fiches est recommandé.</p>
                  </div>
                  <div className="space-y-2">
                    {parStructure.filter(s => s.scorePonderationMoyen < 50 && s.nbProcedures > 0)
                      .sort((a, b) => a.scorePonderationMoyen - b.scorePonderationMoyen)
                      .map(s => (
                        <Link key={s.structureId} to={`/procedures?structureId=${s.structureId}`} className="flex items-center justify-between p-3 rounded-xl border border-amber-100 hover:bg-amber-50/50 transition-colors group">
                          <div>
                            <p className="text-sm font-medium text-gray-900 group-hover:text-primary-600">{s.sigle || s.structureNom}</p>
                            {s.sigle && <p className="text-[11px] text-gray-400">{s.structureNom}</p>}
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-gray-500">{s.nbProcedures} proc.</span>
                            <div className="flex items-center gap-2">
                              <MiniProgress value={s.scorePonderationMoyen} />
                            </div>
                          </div>
                        </Link>
                      ))}
                  </div>
                </Section>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════
              ONG 5 — Agents & RH
          ════════════════════════════════════════ */}
          {activeTab === 'agents' && (
            <div className="space-y-6">

              {/* Résumé RH */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Total agents',   value: kpis.totalUsers,                 cls: 'text-gray-900' },
                  { label: 'Avec tâches',    value: tachesParUtilisateur.length,     cls: 'text-green-600' },
                  { label: 'Sans tâche',     value: agentsSansTache.length,          cls: agentsSansTache.length > 0 ? 'text-amber-600' : 'text-gray-400' },
                  { label: 'Taux affectation', value: kpis.totalUsers > 0 ? `${Math.round(((kpis.totalUsers - agentsSansTache.length) / kpis.totalUsers) * 100)}%` : '—', cls: 'text-primary-600' },
                ].map(item => (
                  <div key={item.label} className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
                    <p className={`text-2xl font-black ${item.cls}`}>{item.value}</p>
                    <p className="text-xs text-gray-500 mt-1">{item.label}</p>
                  </div>
                ))}
              </div>

              {/* Bar chart productivité */}
              <Section title="Charge de travail par agent" subtitle="Tâches assignées vs terminées — Top 10">
                {tachesParUtilisateur.length === 0 ? <EmptyState message="Aucune tâche assignée" /> : (
                  <ResponsiveContainer width="100%" height={Math.max(200, userChartData.length * 38)}>
                    <BarChart data={userChartData} layout="vertical" barCategoryGap="25%">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" width={84}
                        tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} />
                      <ReTooltip content={<CustomBarTooltip />} />
                      <Legend formatter={v => <span className="text-xs text-gray-600">{v}</span>} />
                      <Bar dataKey="Total"     fill="#93C5FD" radius={[0,4,4,0]} maxBarSize={12} />
                      <Bar dataKey="Terminées" fill="#3B82F6" radius={[0,4,4,0]} maxBarSize={12} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Section>

              {/* Table classement agents */}
              {tachesParUtilisateur.length > 0 && (
                <Section title="Classement des agents par productivité" subtitle="Taux de complétion décroissant">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">#</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">Agent</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">Structure</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">Tâches</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2">Taux</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {[...tachesParUtilisateur]
                          .sort((a, b) => b.tauxCompletion - a.tauxCompletion)
                          .map((u: UserProductivity, i) => (
                            <tr key={u.userId} className="hover:bg-gray-50 transition-colors">
                              <td className="py-2 pr-3">
                                <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center
                                  ${i === 0 ? 'bg-yellow-100 text-yellow-700' : i === 1 ? 'bg-gray-100 text-gray-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-50 text-gray-400'}`}>
                                  {i + 1}
                                </span>
                              </td>
                              <td className="py-2 pr-3">
                                <Link to="/utilisateurs" className="font-medium text-gray-900 hover:text-primary-600">{u.prenom} {u.nom}</Link>
                              </td>
                              <td className="py-2 pr-3 text-xs text-gray-500">
                                {u.structureNom
                                  ? <Link to={`/procedures?structureId=${(parStructure.find(s => s.structureNom === u.structureNom)?.structureId) || ''}`} className="hover:text-primary-600">{u.structureNom}</Link>
                                  : '—'}
                              </td>
                              <td className="py-2 pr-3 text-gray-600 text-xs">{u.nbTachesTerminees}/{u.nbTachesTotal}</td>
                              <td className="py-2"><MiniProgress value={u.tauxCompletion} /></td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}

              {/* Agents sans tâche */}
              {agentsSansTache.length > 0 && (
                <Section title={`Agents sans tâche assignée (${agentsSansTache.length})`} subtitle="Ressources disponibles à mobiliser">
                  <div className="flex items-start gap-2 mb-4 bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-3">
                    <InformationCircleIcon className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-700">Ces agents peuvent prendre en charge les tâches en attente ou en retard listées dans l'onglet Risques.</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">Agent</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2 pr-3">Rôle</th>
                          <th className="text-left text-[11px] font-medium text-gray-400 uppercase pb-2">Structure</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {agentsSansTache.slice(astPage * LIST_SIZE, (astPage + 1) * LIST_SIZE).map((u: AgentSansTache) => (
                          <tr key={u.userId} className="hover:bg-gray-50">
                            <td className="py-2 pr-3"><Link to="/utilisateurs" className="font-medium text-gray-900 hover:text-primary-600">{u.prenom} {u.nom}</Link></td>
                            <td className="py-2 pr-3">
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${u.role === 'RESPONSABLE' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                {u.role === 'RESPONSABLE' ? 'Responsable' : 'Utilisateur'}
                              </span>
                            </td>
                            <td className="py-2 text-xs text-gray-500">{u.sigle || u.structureNom || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {agentsSansTache.length > LIST_SIZE && (
                    <div className="mt-3">
                      <Pagination page={astPage} totalPages={Math.ceil(agentsSansTache.length / LIST_SIZE)}
                        totalElements={agentsSansTache.length} size={LIST_SIZE} onPageChange={setAstPage} />
                    </div>
                  )}
                </Section>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════
              ONG 6 — Gantt global
          ════════════════════════════════════════ */}
          {activeTab === 'gantt' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-primary-50 border border-primary-100 rounded-xl px-4 py-3">
                <ViewColumnsIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-primary-800">
                  <span className="font-semibold">Diagramme de Gantt global</span> — Toutes les tâches de toutes les structures sur un seul calendrier.
                  Filtrez par structure, regroupez par structure ou procédure, zoomez pour adapter la vue.
                </div>
              </div>
              <GlobalGanttChart
                taches={ganttTaches}
                structures={ganttStructures}
                isLoading={ganttLoading}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Analytique;
